import isEqual from 'lodash/isEqual';
import type * as PIXI from 'pixi.js';
import { assertIsValidIconSourceData, assertNumberBetween } from '../../validators';
import { Config } from '../../video-data/config';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import uuid from '../../video-data/utils/uuid';
import type { LoadSettings } from '../serializableMixin';
import { getTemplateProperty } from '../attributes';
import { AssetCreatedFrom } from '../../enums';
import type { BaseAsset, BaseAssetSettings } from './BaseAsset';
import type { Container } from './Container';
import { LottieAsset } from './LottieAsset/LottieAsset';

export type IconSourceData = {
    url: string;
    uuid: string;
    animated: boolean | null;
    source: number;
    source_id: string;
    license: string;
};

/**
 * This class is used to render icons using Lottie sources. Icons are not full screen, can be selected and changed by
 * the user. Here the user can change the colors and source of an icon. Even though this functionality could be
 * absorbed by our LottieAsset class, we decided to create a separate class to make it easier for web to work with.
 */
export class IconAsset extends LottieAsset {
    override _classType = Config.ICON_ASSET_TYPE;

    #createdFrom: AssetCreatedFrom = AssetCreatedFrom.SCENE_DESIGN;

    // Shared variables

    #sourceData = {
        uuid: uuid(),
        animated: false,
        url: 'https://storage.googleapis.com/lumen5-prod-lottie/icons/default1.json',
        source: 30, // SOURCE_LUMEN5_ICONS_PREMIUM
        source_id: null,
        license: Config.CC0_LICENSE as string,
    };

    constructor(container: Container, settings: BaseAssetSettings) {
        super(container, settings);
        this.maxWidth = 512;
        this.maxHeight = 512;
        this.fillMode = Config.FILL_CONTAIN;
        this.playbackMode = Config.PLAYBACK_LOOP;
        this.sourceUrl = this.sourceData.url;
        this.#createdFrom = settings.createdFrom ?? this.#createdFrom;
    }

    get sourceData(): IconSourceData {
        return this.#sourceData;
    }

    /**
     * Reset color maps and updates source data.
     *
     * Important:
     * - To be used only on the scene editor because we want to reset color maps anytime designer change sourceData
     * - Everywhere else, use `replace` API to change sourceData
     */
    set sourceData(data: IconSourceData) {
        if (isEqual(this.sourceData, data)) {
            return;
        }

        assertIsValidIconSourceData(data);

        this.#sourceData = data;
        // icon asset is using lottie asset for everything
        // update lottie asset source url so that it can load and render new icon
        // it will clear existing color maps and trigger build step
        this.sourceUrl = data.url;
    }

    get mediaId(): string {
        return this.sourceData.uuid;
    }

    get mediaSourceId(): string {
        return this.sourceData.source_id;
    }

    get license(): string {
        return this.sourceData.license;
    }

    get mediaSource(): number {
        return this.sourceData.source;
    }

    get opacity() {
        return this._pixiContainer.alpha;
    }

    set opacity(value: number) {
        if (value === this._pixiContainer.alpha) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this._pixiContainer.alpha = value;
        this.requiresRedraw = true;
    }

    replace(sourceData: IconSourceData) {
        if (typeof sourceData === 'string') {
            throw new Error('Input cannot be a string');
        }

        assertIsValidIconSourceData(sourceData);

        // save current color maps
        // to use for remapping in build step
        // refer `_build` in lottie asset
        this._lastColorMaps = this.colorMaps;
        // update icon asset source data
        this.sourceData = sourceData;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherIcon = other as IconAsset;
        if (!Config.isIconAsset(otherIcon)) {
            return false;
        }

        if (
            this.playbackMode === Config.PLAYBACK_WAVEFORM ||
            otherIcon.playbackMode === Config.PLAYBACK_WAVEFORM
        ) {
            return false;
        }

        if (this.sourceData.uuid !== otherIcon.sourceData.uuid) {
            return false;
        }

        const samePosition =
            this.transformation.position.x === otherIcon.transformation.position.x &&
            this.transformation.position.y === otherIcon.transformation.position.y;
        const sameScale = this.width === otherIcon.width && this.height === otherIcon.height;
        const sameAlpha = this.alpha === otherIcon.alpha;

        if (samePosition && sameScale && sameAlpha) {
            return false;
        }

        return true;
    }

    /**
     * Returns the approximate biggest possible bounds.
     * If there is a mask, then the bounds of the mask is returned.
     */
    override get approximateMaxBounds(): PIXI.Rectangle {
        const freeParent = this.getFreelyPositionedParent();
        const transformation = freeParent.transformation;
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(transformation, width, height);
        return { x, y, width, height } as PIXI.Rectangle;
    }

    /**
     * Override the _remapColors from LottieAsset to change behaviour for icons.
     * What we want is when _remapColors() is called for us to attempt to copy over the colours
     * from the previous asset to the new asset.
     * Currently we only (really) support a SINGLE target colour. If there are more
     * colours in the target than in the source we do nothing. Otherwise we copy over colours in the same order.
     * This is super basic logic that won't always work, but fine for now.
     */
    override _remapColors() {
        if (!this.isSourceUrlLoaded) {
            return;
        }

        // check to see if the number of colours in the target is more than the source, if so abort!
        if (this.colorMaps.length > this._lastColorMaps.length) {
            // make sure to clear last color maps
            // to skip remapping in next iteration of build loop
            this._lastColorMaps = [];
            return;
        }

        // turn off autoDraw while we update colorMaps to ensure we don't trigger another draw/build
        // before we've cleared _lastColorMaps
        const autoDraw = this.autoDraw;
        this.autoDraw = false;

        // okay, copy the colours over
        for (let i = 0; i < this.colorMaps.length; i++) {
            this.colorMaps[i].targetColor = getTemplateProperty(
                this._lastColorMaps[i],
                'targetColor',
            );
        }

        // make sure to clear last color maps
        // to skip remapping in next iteration of build loop
        this._lastColorMaps = [];

        this.autoDraw = autoDraw;
    }

    /**
     * Load sourceData during initial video data load
     * This method will only be called when video data is first being loaded by Luminary.
     * Unlike sourceData setter, this method ensures that any existing color maps are not cleared.
     */
    _sourceDataCustomLoad(value: IconSourceData, settings: LoadSettings) {
        this.#sourceData = value;
        this._sourceUrlCustomLoad(value.url, settings);
    }

    override get propertiesToSerialize() {
        const dontSerialize = ['sourceUrl'];
        return super.propertiesToSerialize
            .concat(['opacity', 'maxWidth', 'maxHeight', 'createdFrom'])
            .filter((property) => dontSerialize.indexOf(property) === -1);
    }

    /**
     * List of element properties that can be edited by users and would need to be preserved between aspect ratio changes.
     */
    override get editableProperties() {
        return super.editableProperties.concat(['sourceData', 'colorMaps']);
    }

    get createdFrom(): AssetCreatedFrom {
        return this.#createdFrom;
    }

    _createdFromCustomLoad(value: AssetCreatedFrom) {
        this.#createdFrom = value;
    }
}
